import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBranding, useUnreadCount } from "@/hooks/useBranding";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, LogOut, Home } from "lucide-react";
import { cn } from "@/lib/utils";
import { AccessBanner, AccessGate } from "@/components/AccessBanner";

function Brand({ sub, brand }) {
  return (
    <div className="flex items-center gap-2.5" data-testid="app-brand">
      {brand?.logo_url ? (
        <img src={brand.logo_url} alt={brand.name} className="h-9 max-w-[120px] object-contain rounded-md" data-testid="app-brand-logo" />
      ) : (
        <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center shadow-sm">
          <Home className="h-5 w-5 text-primary-foreground" />
        </div>
      )}
      <div className="leading-tight">
        <div className="font-display font-extrabold text-[17px] tracking-tight text-slate-900 truncate max-w-[150px]">{brand?.business_name || "VuZuRent"}</div>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-primary">{sub}</div>
      </div>
    </div>
  );
}

function NavItems({ nav, onClick, unread = 0 }) {
  return (
    <nav className="flex flex-col gap-1">
      {nav.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink key={item.to} to={item.to} end={item.to === "/portal"} onClick={onClick}
            data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) => cn("group flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-all",
              isActive ? "bg-primary text-primary-foreground shadow-sm" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900")}>
            <Icon className="h-[18px] w-[18px]" />
            {item.label}
            {item.badge && unread > 0 && (
              <span className="ml-auto rounded-full bg-rose-500 text-white text-[10px] font-bold px-1.5 py-0.5 min-w-[18px] text-center" data-testid="nav-unread-badge">{unread > 99 ? "99+" : unread}</span>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}

function SignOutButton({ onClick, className = "" }) {
  return (
    <Button variant="ghost" onClick={onClick} data-testid="logout-btn" className={cn("w-full justify-start gap-2 text-slate-600 hover:text-destructive hover:bg-red-50", className)}>
      <LogOut className="h-4 w-4" /> Sign out
    </Button>
  );
}

function UserFooter({ user, onLogout }) {
  const initials = (user?.name || user?.email || "U").slice(0, 2).toUpperCase();
  return (
    <div className="mt-auto pt-4 border-t border-slate-200">
      <div className="flex items-center gap-3 px-1.5 py-2">
        <div className="h-9 w-9 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-xs font-bold">{initials}</div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-800 truncate">{user?.name}</div>
          <div className="text-xs text-slate-500 truncate">{user?.email}</div>
        </div>
      </div>
      <SignOutButton onClick={onLogout} className="mt-1" />
    </div>
  );
}

export default function AppShell({ nav, brandSub }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const brand = useBranding();
  const unread = useUnreadCount(location.pathname);

  return (
    <div className="min-h-screen bg-background">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col border-r border-slate-200 bg-white px-4 py-6">
        <div className="px-1.5 mb-8"><Brand sub={brandSub} brand={brand} /></div>
        <NavItems nav={nav} unread={unread} />
        <UserFooter user={user} onLogout={logout} />
      </aside>

      <header className="lg:hidden sticky top-0 z-40 flex items-center justify-between border-b border-slate-200 bg-white/95 backdrop-blur-md px-4 py-3">
        <Brand sub={brandSub} brand={brand} />
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon" data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 p-5">
            <div className="mb-8"><Brand sub={brandSub} brand={brand} /></div>
            <NavItems nav={nav} onClick={() => setOpen(false)} unread={unread} />
            <SignOutButton onClick={logout} className="mt-6" />
          </SheetContent>
        </Sheet>
      </header>

      <main className="lg:pl-64">
        <div key={location.pathname} className="max-w-7xl mx-auto px-4 sm:px-8 py-6 sm:py-8 animate-fade-up">
          <AccessBanner user={user} />
          <AccessGate><Outlet /></AccessGate>
        </div>
      </main>
    </div>
  );
}
